import React, {FC} from 'react';
import {Icon} from './Icon';
import {Section} from './Section';
import {TCartItem} from "../types/types";
import {addCartItem, getCarts, getProductDetail, removeCartItem} from "../apis/products";
import {useHistory} from "react-router-dom";
import {nToast} from "../plugins/reactToast";
import {Checkbox} from "./Checkbox";
import {Bridge} from "../plugins/bridge";
import {BRAZE_EVENT} from "../constants/braze";


interface CartItemProps {
    item: TCartItem;
    onChange: () => void;
    checked: boolean;
    onChecked: () => void;
}

export const CartItem: FC<CartItemProps> = ({item, onChange, checked, onChecked}) => {
    const history = useHistory();
    const soldOut = item.size.currentStock <= 0;

    const removeItem = async () => {
        var cart_count = 0
        getCarts().then((cart) => cart_count = cart.data.payload.carts.length);

        removeCartItem(item.product.id, item.size.id).then(async r => {
            const product = item.product;
            Bridge.sendBraze(BRAZE_EVENT.DELETED_FROM_CART, {
                item_id: String(product?.id),
                item_name: String(product?.name),
                item_category: await getProductDetail(product.id).then((data) => {return data.data.payload.categoryCode}),
                item_color: String(product?.color),
                item_gender: await getProductDetail(product.id).then((data) => {return data.data.payload.genderCode}),
                item_size: item.size.name,
                item_price: Number(product?.initialPrice),
                item_discount_rate: [Number((product?.initialPrice - product?.currentPrice) / product?.initialPrice) * 100],
                item_discount_price: Number(product?.currentPrice),
                order_item_quantity : item.count
            });

            const itemEventProperties = {
                item_id: [String(product?.id)],
                item_name: [String(product?.name)],
                item_category: [await getProductDetail(product.id).then((data) => {return data.data.payload.categoryCode})],
                item_color: [String(product?.color)],
                item_gender: [await getProductDetail(product.id).then((data) => {return data.data.payload.genderCode})],
                item_size: [item.size.name],
                item_price: [Number(product?.initialPrice)],
                item_discount_rate: [Number((product?.initialPrice - product?.currentPrice) / product?.initialPrice) * 100],
                item_discount_price: [Number(product?.currentPrice)],
                order_item_quantity : [item.count]
            }

            Bridge.sendAmplitude('setUserProperties#total_cart_items_count', cart_count - 1);
            Bridge.sendAmplitude('item_deleted_from_cart', itemEventProperties);

            onChange();
        })
    }

    const changeCount = (count: number) => {
        // count 에 입력되는 값은 1 또는 -1 이지만,
        // 일치조건 보다는 범위조건으로 의미를 추론합니다.
        const availableCount = item.size.currentStock;
        const targetCount = item.count + count;

        // 수량 차감시 마지막 수량은 [삭제] 버튼으로만 차감할 수 있다.
        if (targetCount < 1) return; // if (item.count === 1 && count === -1) return; // silence

        // 재고수량을 넘어선 장바구니 수량은 요청할 수 없다.
        if (availableCount < targetCount) {
            nToast('재고가 부족합니다');
            return;
        }

        addCartItem(item.product.id, {sizeId: item.size.id, count: count}).then(r => onChange());
    }

    if (!item) return null;
    return (
        <>
            {/* Mobile */}
            <div className="flex space-x-4 md:hidden pb-6 mb-6">
                <Checkbox className="bg-white border border-gray-300"
                          checked={!soldOut && checked}
                          onChange={onChecked}
                          disabled={soldOut}
                />
                <div className="w-1/2 aspect-w-5 aspect-h-2 rounded-md overflow-hidden"
                     onClick={() => history.push(`/product/${item.product.id}`)}
                >
                    <img
                        src={item.product.thumbnailUrl}
                        alt=""
                        className="w-full h-full object-cover"
                    />
                </div>

                <div className="flex flex-col w-full">
                    <div className="flex justify-between flex-1">
                        <div className="font-bold text-10">
                            <p className="">{item.product.name}</p>
                            <p className="text-brand-3 ">{item.product.color}</p>
                            <p className="text-brand-3 ">Size {item.size.name}</p>
                            <p className="text-brand-3 ">
                                {item.size.currentStock === 1 ? '마지막 재고*' : ' '}
                                {soldOut ? '이 사이즈는 품절되었습니다.*' : ' '}
                            </p>
                        </div>

                        <button className="bg-gray-100 rounded-full wh-6 grid place-content-center"
                                onClick={removeItem}
                        >
                            <Icon.X/>
                        </button>
                    </div>

                    <div className={`flex justify-between ${soldOut ? 'items-baseline' : 'items-end'}`}>
                        {soldOut ? (
                          <div className="text-14 line-through text-red-500">사이즈 품절</div>
                        ) : (
                          <div className="bg-gray-100 rounded-md text-14 font-bold">
                              <button className="p-2" onClick={() => changeCount(-1)}>-</button>
                              <button className="p-2">{item.count}</button>
                              <button className="p-2" onClick={() => changeCount(1)}>+</button>
                          </div>
                        )}

                        <p className="text-red-500 font-bold">{item.product.currentPrice.toLocaleString()}원</p>
                    </div>
                </div>
            </div>

            {/* Desktop */}
            <div className="hidden md:grid grid-cols-7 space-x-2 items-center gap-x-6">
                <Checkbox className="bg-white border border-gray-300"
                          checked={!soldOut && checked}
                          onChange={onChecked}
                          disabled={soldOut}
                />
                <div className=" aspect-w-6 aspect-h-7 rounded-md overflow-hidden"
                     onClick={() => history.push(`/product/${item.product.id}`)}
                >
                    <img
                        src={item.product.thumbnailUrl}
                        alt=""
                        className="object-cover"
                    />
                </div>

                <Section className="col-span-2 space-y-3">
                    <p className="text-14 font-bold">{item.product.name}</p>
                    <div className="text-14 flex justify-between">
                        <p className="">{item.product.color}</p>
                        <p className="pr-16">사이즈 {item.size.name}</p>
                    </div>
                    <p className="text-13 text-gray-400">
                        {item.size.currentStock === 1 ? '마지막 재고*' : ' '}
                        {soldOut ? '이 사이즈는 품절되었습니다.*' : ' '}
                    </p>
                </Section>

                <Section className="flex space-x-2 items-center justify-center">
                    {soldOut ? (
                      <p className="text-14 line-through text-red-500">사이즈 품절</p>
                    ) : (
                      <>
                          <p className="">수량:</p>
                          <div className="bg-gray-100 rounded-md text-14 font-bold">
                              <button className="p-2" onClick={() => changeCount(-1)}>-</button>
                              <button className="p-2">{item.count}</button>
                              <button className="p-2" onClick={() => changeCount(1)}>+</button>
                          </div>
                      </>
                    )}
                </Section>

                <Section className="grid place-content-center">
                    <button className="underline text-14"
                            onClick={removeItem}
                    >삭제
                    </button>
                </Section>

                <Section className="grid place-content-center">
                    <p className="text-14 font-bold">{item.product.currentPrice.toLocaleString()}원</p>
                </Section>
            </div>
        </>
    );
};
